import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './AllMessages.css';

const EMPTY_PAGINATION = { page: 1, limit: 20, total: 0, totalPages: 1, hasNextPage: false, hasPrevPage: false };

const formatDate = (value) => {
  const date = new Date(value);
  const today = new Date();
  return date.toDateString() === today.toDateString()
    ? date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' });
};

const readStatus = {
  read: { label: 'Gelezen', className: 'am-read-status-read' },
  partially_read: { label: 'Deels gelezen', className: 'am-read-status-partial' },
  unread: { label: 'Ongelezen', className: 'am-read-status-unread' },
};

const AllMessages = ({ type = 'inbox' }) => {
  const { userInfo } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('personal');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState(EMPTY_PAGINATION);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const token = sessionStorage.getItem('jwtToken');
  const navigate = useNavigate();
  const isInbox = type === 'inbox';
  const isAdmin = ['super_admin', 'locatie_admin'].includes(userInfo?.role);
  const isAdminView = viewMode === 'admin';

  axios.defaults.withCredentials = true;

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!isAdmin) setViewMode('personal');
  }, [isAdmin]);

  useEffect(() => {
    let active = true;
    setMessages([]);
    setLoading(true);
    setError('');

    const request = isAdminView
      ? axios.get(`${process.env.REACT_APP_BACKEND_URL}/messages/admin/all`, {
          params: { page, limit, search: debouncedSearch },
          headers: { Authorization: `Bearer ${token}` },
        })
      : axios.get(`${process.env.REACT_APP_BACKEND_URL}${isInbox ? '/messages/inbox' : '/messages/sent'}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

    request.then((response) => {
      if (!active) return;
      if (isAdminView) {
        setMessages(Array.isArray(response.data?.data) ? response.data.data : []);
        setPagination(response.data?.pagination || { ...EMPTY_PAGINATION, limit });
      } else {
        setMessages(Array.isArray(response.data) ? response.data : []);
        setPagination(EMPTY_PAGINATION);
      }
    }).catch((requestError) => {
      console.error('Fout bij ophalen berichten:', requestError);
      if (active) setError(requestError.response?.data?.error || 'De berichten konden niet worden geladen. Probeer het opnieuw.');
    }).finally(() => {
      if (active) setLoading(false);
    });

    return () => { active = false; };
  }, [isInbox, isAdminView, page, limit, debouncedSearch, token]);

  const showAdminView = () => {
    setViewMode('admin');
    setPage(1);
    setSearchTerm('');
  };

  const showPersonalView = () => {
    setViewMode('personal');
    setPage(1);
    setSearchTerm('');
  };

  const pageNumbers = Array.from({ length: Math.min(5, pagination.totalPages || 1) }, (_, index) => {
    const start = Math.min(Math.max(1, pagination.page - 2), Math.max(1, pagination.totalPages - 4));
    return start + index;
  }).filter((number) => number <= pagination.totalPages);

  const title = isAdminView ? 'Alle locatieberichten' : (isInbox ? 'Ontvangen berichten' : 'Verzonden berichten');
  const subtitle = isAdminView
    ? 'Toezichtsoverzicht van berichten die aan jouw organisatie of locatie(s) zijn gekoppeld.'
    : (isInbox ? 'Bekijk en beantwoord berichten.' : 'Een overzicht van de berichten die je hebt verstuurd.');

  return (
    <main className="am-page">
      <div className="am-shell">
        <header className="am-heading">
          <div><span className="am-eyebrow">Communicatie</span><h1>{title}</h1><p>{subtitle}</p></div>
          <div className="am-heading-actions">
            {isAdmin && !isAdminView && <button type="button" className="am-admin-button" onClick={showAdminView}><i className="bi bi-shield-check" /><span>Haal alle berichten</span></button>}
            {isAdminView && <button type="button" className="am-admin-button am-admin-button-active" onClick={showPersonalView}><i className="bi bi-person" /><span>Mijn berichten</span></button>}
            <Link to="/newmessage" className="am-compose-button"><i className="bi bi-pencil-square" aria-hidden="true" /><span>Nieuw bericht</span></Link>
          </div>
        </header>

        {!isAdminView && <nav className="am-tabs" aria-label="Berichtmappen">
          <button type="button" className={`am-tab ${isInbox ? 'am-tab-active' : ''}`} onClick={() => navigate('/messages/inbox')} aria-current={isInbox ? 'page' : undefined}><i className="bi bi-inbox" />Inbox</button>
          <button type="button" className={`am-tab ${!isInbox ? 'am-tab-active' : ''}`} onClick={() => navigate('/messages/sent')} aria-current={!isInbox ? 'page' : undefined}><i className="bi bi-send" />Verzonden</button>
        </nav>}

        {isAdminView && <div className="am-admin-toolbar">
          <label className="am-admin-search"><i className="bi bi-search" /><span className="am-visually-hidden">Zoeken</span><input type="search" placeholder="Zoek op afzender, ontvanger of onderwerp…" value={searchTerm} onChange={(event) => { setSearchTerm(event.target.value); setPage(1); }} /></label>
          <label className="am-admin-limit"><span>Per pagina</span><select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }}><option value={10}>10</option><option value={20}>20</option><option value={50}>50</option><option value={100}>100</option></select></label>
        </div>}

        {isAdminView && !loading && !error && <div className="am-admin-summary">{pagination.total} bericht{pagination.total === 1 ? '' : 'en'} gevonden</div>}

        <section className="am-panel" aria-busy={loading}>
          {loading && <div className="am-state" role="status"><span className="am-loader" /><strong>Berichten laden</strong><span>Even geduld…</span></div>}
          {!loading && error && <div className="am-state am-state-error" role="alert"><span className="am-state-icon"><i className="bi bi-exclamation-circle" /></span><strong>Laden mislukt</strong><span>{error}</span></div>}
          {!loading && !error && messages.length === 0 && <div className="am-state"><span className="am-state-icon"><i className="bi bi-envelope-open" /></span><strong>Geen berichten gevonden</strong><span>{isAdminView ? 'Er zijn geen berichten binnen het toegestane bereik.' : (isInbox ? 'Nieuwe berichten verschijnen hier automatisch.' : 'Verstuur je eerste bericht om te beginnen.')}</span></div>}

          {!loading && !error && messages.length > 0 && <div className="am-list" role="list">
            {messages.map((message) => {
              const personalUnread = !isAdminView && isInbox && !message.is_read;
              const status = readStatus[message.read_status] || readStatus.unread;
              const showReadStatus = isAdminView || !isInbox;
              const personLabel = isAdminView ? message.sender_name : (isInbox ? message.sender_name : message.recipients);
              const secondaryLabel = isAdminView ? `Aan: ${message.recipients || 'Onbekend'}` : message.subject;

              return <button type="button" role="listitem" key={message.message_id} className={`am-message ${personalUnread ? 'am-message-unread' : ''} ${isAdminView ? 'am-message-admin' : ''}`} onClick={() => navigate(`/viewmessage/${message.message_id}${isAdminView ? '?adminView=1' : ''}`)}>
                <span className="am-message-marker" />
                <span className="am-message-icon"><i className={`bi ${isAdminView ? 'bi-shield-check' : (isInbox ? 'bi-person' : 'bi-send')}`} /></span>
                <span className="am-message-content">
                  <span className="am-message-topline"><span className="am-message-person">{personLabel || 'Onbekend'}</span><time dateTime={message.created_at}>{formatDate(message.created_at)}</time></span>
                  {isAdminView && <span className="am-message-route">{secondaryLabel}</span>}
                  <span className="am-message-subject">{message.subject || <em>Geen onderwerp</em>}</span>
                </span>
                <span className="am-message-status">{showReadStatus ? <span className={`am-read-status ${status.className}`}>{status.label}</span> : (personalUnread && <span className="am-unread-label">Nieuw</span>)}<i className="bi bi-chevron-right" /></span>
              </button>;
            })}
          </div>}
        </section>

        {isAdminView && !loading && !error && pagination.totalPages > 1 && <nav className="am-pagination" aria-label="Paginering">
          <button type="button" disabled={!pagination.hasPrevPage} onClick={() => setPage((value) => Math.max(1, value - 1))}><i className="bi bi-chevron-left" /><span>Vorige</span></button>
          <div>{pageNumbers.map((number) => <button type="button" key={number} className={number === pagination.page ? 'am-page-active' : ''} onClick={() => setPage(number)} aria-current={number === pagination.page ? 'page' : undefined}>{number}</button>)}</div>
          <button type="button" disabled={!pagination.hasNextPage} onClick={() => setPage((value) => value + 1)}><span>Volgende</span><i className="bi bi-chevron-right" /></button>
        </nav>}
      </div>
    </main>
  );
};

export default AllMessages;
